import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { AuditSettings } from '../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { combineLatest, Observable, of, Subscription, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { AuditSettingsContext } from '../../contexts/audit-settings.context';
import { GlobalPropertiesContext } from '../../../shared/contexts/global-properties.context';
import { Router } from '@angular/router';

@Component({
  selector: 'ziro-audit-settings',
  templateUrl: './audit-settings.component.html',
  styleUrls: ['../../admin-page.scss'],
  standalone: false,
})
export class AuditSettingsComponent extends ZiroFormAbstractDirective<AuditSettings> implements OnInit, OnDestroy {
  protected _ziroFormStateService: ZiroFormStateService;
  private breadcrumbsService = inject(BreadcrumbsService);
  private bottomNavService = inject(BottomNavService);
  private toastService = inject(ToastService);
  private auditSettingsContext = inject(AuditSettingsContext);
  private _globalPropertiesContext = inject(GlobalPropertiesContext);
  private _router = inject(Router);

  isLoading = true;
  ziroIcons = ZiroIcons;
  isPageDisabled = false;

  formConfig = {
    fields: {
      daysToKeepAuditRecords: {
        required: true,
        dataAutomation: 'days-to-keep-audit-records',
        label: 'tkey;admin.system.cisco_general_settings.audit_days_to_keep.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        validation: [
          {
            validator: (val: number) => (val >= 1 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID),
            message: 'tkey;admin.audits.logs.audit_days_to_keep.positive.error.text',
          },
        ],
        options: {
          content: 'tkey;admin.system.cisco_general_settings.audit_days_to_keep.content',
        },
      },
    },
  };

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.audits.settings.title' }]);

    const sub = this._globalPropertiesContext.state$
      .pipe(
        switchMap((globalProperties) => {
          this.isPageDisabled = globalProperties.hostedEnabled && this._router.url.includes('admin/audits/settings');
          if (this.isPageDisabled) {
            this.isLoading = false;
            return of([]);
          } else {
            return this.auditSettingsContext.state$.pipe(
              tap((settings) => {
                this.entitySource.next(settings);
                this.isLoading = false;
              }),
            );
          }
        }),
      )
      .subscribe();
    this._subscriptions.add(sub);

    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'saveAuditSettings',
          dataAutomation: 'save-audit-settings-button',
          label: 'tkey;admin.ui.save',
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: {
              disabled: false,
              tooltipKey: '',
            },
          },
          icon: this.ziroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );

    const validationAlertSub = combineLatest([
      this.ziroFormsUpdateSource.asObservable(),
      this._validateAndSubmitSource,
    ]).subscribe(() => {
      this.bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subscriptions.add(validationAlertSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  submit(): Observable<void> {
    return this._saveAuditSettings();
  }

  private _saveAuditSettings(): Observable<void> {
    this._setButtonPending(true);

    return this.auditSettingsContext.update(this.entity).pipe(
      tap(() => {
        this._setButtonPending(false);

        this.toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.AUDITS,
          'tkey;shared.toast.save.success.title',
          'tkey;admin.audits.settings.toast.message',
        );
      }),
    );
  }

  private _setButtonPending(isPending: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'saveAuditSettings',
        state: {
          pending: isPending,
        },
      }),
    );
  }
}
